/**
 * Marina Escrow Dashboard - Task-based staff payroll processor
 * Marina is software — earns nothing. She processes payroll from the 1.88% escrow.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Banknote, CheckCircle2, Clock, Coins,
  DollarSign, Loader2, Plus, RefreshCw, Search,
  Send, User, Users, Wallet, History, Gift,
} from 'lucide-react';
import {
  getMarinaEscrowSummary, getMarinaStaff, registerMarinaStaff,
  getPendingPayroll, processPayroll, getPayrollHistory,
} from '@/lib/v2Api';

const fmt = (n) => new Intl.NumberFormat().format(n || 0);

// ─── Escrow Overview Cards ───
const EscrowOverview = ({ summary }) => {
  const utilizationPct = summary.total_accumulated > 0
    ? ((summary.total_distributed / summary.total_accumulated) * 100).toFixed(1)
    : 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="escrow-overview">
      {[
        { title: 'Escrow Balance', value: fmt(summary.balance), sub: 'Available for payroll', icon: Wallet, color: 'text-[hsl(var(--web3-primary))]' },
        { title: 'Total Accumulated', value: fmt(summary.total_accumulated), sub: 'From 1.88% bookings', icon: Coins, color: 'text-emerald-500' },
        { title: 'Total Distributed', value: fmt(summary.total_distributed), sub: `${utilizationPct}% utilization`, icon: Send, color: 'text-blue-500' },
        { title: 'Pending Payout', value: fmt(summary.total_pending_payout), sub: `${summary.staff_with_pending} staff waiting`, icon: Clock, color: 'text-amber-500' },
      ].map(({ title, value, sub, icon: Icon, color }) => (
        <Card key={title} className="card-hover-lift" data-testid={`kpi-${title.toLowerCase().replace(/ /g, '-')}`}>
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
                <p className="text-2xl md:text-3xl heading-font tabular-nums font-semibold">{value}</p>
                <p className="text-xs text-muted-foreground">{sub}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-[hsl(var(--web3-primary))]/10 flex items-center justify-center shrink-0">
                <Icon className={`h-5 w-5 ${color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ─── Staff Roster ───
const StaffRoster = ({ staff, onAddStaff }) => (
  <Card data-testid="staff-roster-card">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <Users className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
          Staff Roster ({staff.length})
        </CardTitle>
        <Button size="sm" onClick={onAddStaff} data-testid="add-staff-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Staff
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <ScrollArea className="h-[300px]">
        {staff.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No staff registered yet</p>
          </div>
        ) : (
          <div className="space-y-1">
            {staff.map((s) => (
              <div key={s.staff_id} className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`staff-${s.staff_id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-[hsl(var(--web3-surface-2))]/80 flex items-center justify-center">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{s.name}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] capitalize">{s.role}</Badge>
                      <span className="text-[10px] text-muted-foreground">{s.pay_schedule}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm heading-font tabular-nums font-medium">
                    {s.pending_payout > 0 ? (
                      <span className="text-amber-500">{fmt(s.pending_payout)} pending</span>
                    ) : (
                      <span className="text-muted-foreground">0 pending</span>
                    )}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {fmt(s.total_earned)} earned | {s.total_tasks_completed} tasks
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </CardContent>
  </Card>
);

// ─── Pending Payroll ───
const PendingPayroll = ({ pending, onProcess, processing }) => (
  <Card data-testid="pending-payroll-card">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <Banknote className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
          Pending Payroll
        </CardTitle>
        {pending?.staff_pending?.length > 0 && (
          <Button
            size="sm"
            onClick={onProcess}
            disabled={processing || !pending.can_process}
            data-testid="process-payroll-btn"
          >
            {processing ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Processing...</>
            ) : (
              <><Send className="h-4 w-4 mr-1" /> Process Payroll</>
            )}
          </Button>
        )}
      </div>
    </CardHeader>
    <CardContent>
      {!pending?.staff_pending?.length ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-500/30 mb-3" />
          <p className="text-sm text-muted-foreground">All caught up — no pending payroll</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Summary Bar */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--web3-surface-2))]/60">
            <div>
              <p className="text-sm font-medium">Total to distribute</p>
              <p className="text-xs text-muted-foreground">Escrow: {fmt(pending.escrow_balance)} HERMES</p>
            </div>
            <div className="text-right">
              <p className="text-xl heading-font tabular-nums font-bold">{fmt(pending.total_pending)}</p>
              <Badge variant={pending.can_process ? 'default' : 'destructive'} className="text-[10px]">
                {pending.can_process ? 'Funds Available' : 'Insufficient Funds'}
              </Badge>
            </div>
          </div>

          {!pending.can_process && (
            <div className="text-xs text-red-400 bg-red-500/10 px-3 py-2 rounded-md">
              Shortfall: {fmt(pending.total_pending - pending.escrow_balance)} HERMES. More bookings needed to fund escrow.
            </div>
          )}

          <ScrollArea className="h-[200px]">
            {pending.staff_pending.map((s) => (
              <div key={s.staff_id} className="flex items-center justify-between py-2.5">
                <div>
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{s.role} | {s.total_tasks_completed} tasks</p>
                </div>
                <p className="text-sm heading-font tabular-nums font-semibold text-amber-500">
                  {fmt(s.pending_payout)} <span className="text-xs text-muted-foreground font-normal">HERMES</span>
                </p>
              </div>
            ))}
          </ScrollArea>
        </div>
      )}
    </CardContent>
  </Card>
);

// ─── Payroll History ───
const PayrollHistorySection = ({ batches }) => (
  <Card data-testid="payroll-history-card">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg heading-font flex items-center gap-2">
        <History className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
        Payroll History
      </CardTitle>
    </CardHeader>
    <CardContent>
      <ScrollArea className="h-[300px]">
        {batches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <History className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No payroll batches processed yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {batches.map((batch) => (
              <div key={batch.batch_id} className="p-3 rounded-lg border bg-card" data-testid={`batch-${batch.batch_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] font-mono">{batch.batch_id}</Badge>
                    <Badge className="text-[10px] bg-emerald-500/15 text-emerald-500 border-emerald-500/30">
                      {batch.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(batch.processed_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">{batch.staff_count} staff paid</span>
                  <span className="heading-font tabular-nums font-semibold">{fmt(batch.total_amount)} HERMES</span>
                </div>
                <div className="space-y-1">
                  {batch.staff_payments.map((p) => (
                    <div key={p.staff_id} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{p.name} <span className="capitalize">({p.role})</span></span>
                      <span className="tabular-nums font-medium">{fmt(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </CardContent>
  </Card>
);

// ─── Add Staff Dialog ───
const AddStaffDialog = ({ open, onClose, hotelId, onAdded }) => {
  const [form, setForm] = useState({ staff_id: '', name: '', role: 'housekeeping', pay_schedule: 'biweekly' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.staff_id || !form.name) { toast.error('Staff ID and Name required'); return; }
    setSubmitting(true);
    try {
      await registerMarinaStaff({ ...form, hotel_id: hotelId });
      toast.success(`${form.name} registered for payroll`);
      setForm({ staff_id: '', name: '', role: 'housekeeping', pay_schedule: 'biweekly' });
      onAdded();
      onClose();
    } catch { toast.error('Failed to register staff'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="add-staff-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font">Register Staff for Payroll</DialogTitle>
          <DialogDescription>Add a staff member to Marina's payroll system.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Staff ID</Label>
            <Input placeholder="staff-001" value={form.staff_id} onChange={e => setForm(p => ({ ...p, staff_id: e.target.value }))} data-testid="input-staff-id" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Full Name</Label>
            <Input placeholder="Maria Garcia" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="input-staff-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Role</Label>
            <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
              <SelectTrigger data-testid="select-staff-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['housekeeping', 'front_desk', 'maintenance', 'concierge', 'kitchen', 'security'].map(r => (
                  <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Pay Schedule</Label>
            <Select value={form.pay_schedule} onValueChange={v => setForm(p => ({ ...p, pay_schedule: v }))}>
              <SelectTrigger data-testid="select-pay-schedule"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['weekly', 'biweekly', 'monthly'].map(s => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-staff-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Register'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Loading Skeleton ───
const DashSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[1,2,3,4].map(i => (
        <Card key={i}><CardContent className="pt-5 pb-4 space-y-3">
          <Skeleton className="h-3 w-24" /><Skeleton className="h-8 w-32" /><Skeleton className="h-3 w-40" />
        </CardContent></Card>
      ))}
    </div>
  </div>
);

// ─── Main Page ───
export default function MarinaEscrowPage() {
  const navigate = useNavigate();
  const [hotelId, setHotelId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [summary, setSummary] = useState(null);
  const [staff, setStaff] = useState([]);
  const [pending, setPending] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);

  const loadData = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const [s, st, p, h] = await Promise.all([
        getMarinaEscrowSummary(id),
        getMarinaStaff(id),
        getPendingPayroll(id),
        getPayrollHistory(id),
      ]);
      setSummary(s);
      setStaff(st.staff || []);
      setPending(p);
      setBatches(h.batches || []);
      setHotelId(id);
    } catch { toast.error('Failed to load escrow data'); }
    finally { setLoading(false); }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) loadData(searchInput.trim());
  };

  const handleProcess = async () => {
    setProcessing(true);
    try {
      await processPayroll(hotelId);
      toast.success('Payroll processed successfully');
      loadData(hotelId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Payroll failed');
    } finally { setProcessing(false); }
  };

  return (
    <div className="admin-theme min-h-dvh bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl heading-font font-semibold tracking-tight" data-testid="page-title">
                Marina Escrow
              </h1>
              <p className="text-xs text-muted-foreground">Automated Staff Payroll Processor</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hotelId && (
              <Button variant="outline" size="sm" onClick={() => loadData(hotelId)} data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 max-w-md" data-testid="hotel-search-form">
          <Input
            placeholder="Enter Hotel ID (e.g. grand-marina-test)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="text-sm"
            data-testid="hotel-id-input"
          />
          <Button type="submit" size="sm" variant="outline" data-testid="search-btn">
            <Search className="h-4 w-4" />
          </Button>
        </form>

        {loading ? <DashSkeleton /> : !summary ? (
          <div className="flex flex-col items-center justify-center py-20 text-center space-y-3">
            <Gift className="h-12 w-12 text-muted-foreground/40" />
            <p className="text-lg heading-font text-muted-foreground">Enter a Hotel ID to view Marina Escrow</p>
            <p className="text-sm text-muted-foreground max-w-md">
              Marina processes payroll from the 1.88% escrow accumulated per booking.
              Staff earn based on completed tasks.
            </p>
          </div>
        ) : (
          <>
            <EscrowOverview summary={summary} />

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <StaffRoster staff={staff} onAddStaff={() => setShowAddStaff(true)} />
              </div>
              <div className="lg:col-span-7">
                <PendingPayroll pending={pending} onProcess={handleProcess} processing={processing} />
              </div>
            </div>

            <PayrollHistorySection batches={batches} />
          </>
        )}
      </div>

      <AddStaffDialog
        open={showAddStaff}
        onClose={() => setShowAddStaff(false)}
        hotelId={hotelId}
        onAdded={() => loadData(hotelId)}
      />
    </div>
  );
}
